import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Papa from "papaparse";
import { GenericModal } from "@/components/GenericModal/GenericModal";
import { GenericButton } from "@/components/GenericButton/GenericButton";
import {
  importSalesCsv,
  downloadSkippedRowsCsv,
  ImportSalesCsvResponse,
  SalesCsvMapping,
  SalesCsvSeparators,
} from "@/api/salesImport";
import "./CsvSalesImportModal.scss";

type Step = "file" | "separators" | "mapping" | "result";

const NOT_PROVIDED = "__not_provided__";

type MappingState = {
  accountName?: string;
  contactEmail?: string;
  amount?: string;
  closeDate?: string;
  soldItems?: string;
};

export const CsvSalesImportModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>("file");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<"success" | "error" | null>(null);
  const [importReport, setImportReport] = useState<ImportSalesCsvResponse | null>(
    null,
  );
  const [headers, setHeaders] = useState<string[]>([]);
  const [separators, setSeparators] = useState<SalesCsvSeparators>({
    delimiter: ";",
    quote: '"',
  });
  const [mapping, setMapping] = useState<MappingState>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = useCallback(() => {
    setStep("file");
    setFile(null);
    setError(null);
    setResult(null);
    setImportReport(null);
    setHeaders([]);
    setSeparators({ delimiter: ";", quote: '"' });
    setMapping({});
    setIsSubmitting(false);
  }, []);

  const close = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const canGoBack = step !== "file";
  const goBack = () => {
    if (step === "mapping") setStep("separators");
    else if (step === "separators") setStep("file");
  };

  const readFileTextWithFallback = useCallback(async (f: File) => {
    const buf = await f.arrayBuffer();
    const bytes = new Uint8Array(buf);
    // Use UTF-8 in "fatal" mode to reliably detect invalid sequences.
    // (Checking for "�" is not enough: the file may legitimately contain it.)
    try {
      return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    } catch {
      // CSV exports from Excel are frequently Windows-1252 (close to latin1).
      try {
        return new TextDecoder("windows-1252").decode(bytes);
      } catch {
        return new TextDecoder("iso-8859-1").decode(bytes);
      }
    }
  }, []);

  const detectSeparators = useCallback(
    async (f: File): Promise<SalesCsvSeparators> => {
      const text = await readFileTextWithFallback(f);
      const delimiters = [";", ",", "\t", "|"] as const;
      const quotes = ['"', "'"] as const;

      let best:
        | {
            delimiter: string;
            quote: string;
            score: number;
          }
        | null = null;

      for (const delimiter of delimiters) {
        for (const quote of quotes) {
          const res = Papa.parse<Record<string, string>>(text, {
            header: true,
            skipEmptyLines: true,
            preview: 2,
            delimiter,
            quoteChar: quote,
          });
          const fields = res.meta.fields ?? [];
          // Heuristic: maximize number of detected columns, and penalize
          // headers that still contain wrapping quotes (often a sign of wrong quoteChar).
          const quotedFields = fields.filter(
            (f) =>
              f.startsWith("'") ||
              f.endsWith("'") ||
              f.startsWith('"') ||
              f.endsWith('"'),
          ).length;
          const score = fields.length * 100 - quotedFields * 10;

          if (!best || score > best.score) {
            best = { delimiter, quote, score };
          }
        }
      }

      return {
        delimiter: best?.delimiter ?? ";",
        quote: best?.quote ?? '"',
      };
    },
    [readFileTextWithFallback],
  );

  const parseHeadersWithSeparators = useCallback(
    async (f: File, nextSeparators: SalesCsvSeparators) => {
      const text = await readFileTextWithFallback(f);
      const res = Papa.parse<Record<string, string>>(text, {
        header: true,
        skipEmptyLines: true,
        preview: 2,
        delimiter: nextSeparators.delimiter,
        quoteChar: nextSeparators.quote ?? '"',
      });
      const cols = res.meta.fields ?? [];
      setHeaders(cols);
      return cols;
    },
    [readFileTextWithFallback],
  );

  const onSelectFile = useCallback(
    async (f: File | null) => {
      setError(null);
      setResult(null);
      if (!f) return;
      if (!f.name.toLowerCase().endsWith(".csv")) {
        setError(t("pages.integrations.csvImport.errors.notCsv"));
        return;
      }

      setFile(f);
      const detected = await detectSeparators(f);
      setSeparators(detected);
      await parseHeadersWithSeparators(f, detected);
      setStep("mapping");
    },
    [detectSeparators, parseHeadersWithSeparators, t],
  );

  const hasAtLeastOneIdentity =
    (mapping.contactEmail && mapping.contactEmail !== NOT_PROVIDED) ||
    (mapping.accountName && mapping.accountName !== NOT_PROVIDED);

  const isMappingValid =
    hasAtLeastOneIdentity &&
    mapping.amount &&
    mapping.closeDate &&
    mapping.soldItems;

  const onNextFromSeparators = useCallback(async () => {
    if (!file) return;
    setError(null);
    try {
      await parseHeadersWithSeparators(file, separators);
      setStep("mapping");
    } catch {
      setError(t("pages.integrations.csvImport.errors.submitFailed"));
    }
  }, [file, parseHeadersWithSeparators, separators, t]);

  const onSubmit = useCallback(async () => {
    if (!file) return;
    if (!isMappingValid) return;

    setIsSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res = await importSalesCsv({
        file,
        separators,
        mapping: {
          accountName:
            mapping.accountName === NOT_PROVIDED
              ? ""
              : (mapping.accountName ?? ""),
          contactEmail:
            mapping.contactEmail === NOT_PROVIDED
              ? ""
              : (mapping.contactEmail ?? ""),
          amount: mapping.amount ?? "",
          closeDate: mapping.closeDate ?? "",
          soldItems: mapping.soldItems ?? "",
        } as SalesCsvMapping,
      });
      setImportReport(res);
      setResult("success");
      setStep("result");
    } catch {
      setError(t("pages.integrations.csvImport.errors.submitFailed"));
      setResult("error");
      setStep("result");
    } finally {
      setIsSubmitting(false);
    }
  }, [file, isMappingValid, mapping, separators, t]);

  const onDownloadSkippedRows = useCallback(async () => {
    if (!importReport?.importId) return;
    setError(null);
    try {
      const blob = await downloadSkippedRowsCsv(importReport.importId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "lignes-ignorees.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(t("pages.integrations.csvImport.errors.downloadFailed"));
    }
  }, [importReport, t]);

  const title = t("pages.integrations.csvImport.title");

  const content = useMemo(() => {
    if (step === "file") {
      return (
        <div className="csv-sales-import-modal">
          <div className="csv-sales-import-modal-text">
            {t("pages.integrations.csvImport.steps.file.message")}
          </div>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => onSelectFile(e.target.files?.[0] ?? null)}
          />
        </div>
      );
    }

    if (step === "separators") {
      return (
        <div className="csv-sales-import-modal">
          <div className="csv-sales-import-modal-text">
            {t("pages.integrations.csvImport.steps.separators.message")}
          </div>

          <div className="csv-sales-import-modal-row">
            <label className="csv-sales-import-modal-label">
              {t("pages.integrations.csvImport.steps.separators.delimiter")}
            </label>
            <select
              value={separators.delimiter}
              onChange={(e) =>
                setSeparators((prev) => ({
                  ...prev,
                  delimiter: e.target.value,
                }))
              }
            >
              <option value=";">;</option>
              <option value=",">,</option>
              <option value="\t">
                {t("pages.integrations.csvImport.steps.separators.tab")}
              </option>
              <option value="|">|</option>
            </select>
          </div>

          <div className="csv-sales-import-modal-row">
            <label className="csv-sales-import-modal-label">
              {t("pages.integrations.csvImport.steps.separators.quote")}
            </label>
            <select
              value={separators.quote ?? '"'}
              onChange={(e) =>
                setSeparators((prev) => ({ ...prev, quote: e.target.value }))
              }
            >
              <option value={'"'}>"</option>
              <option value={"'"}>'</option>
            </select>
          </div>
        </div>
      );
    }

    if (step === "result") {
      const isSuccess = result === "success";
      const totalRows = importReport?.totalRows ?? 0;
      const importedCount = importReport?.importedCount ?? 0;
      const skippedCount = importReport?.skippedCount ?? 0;
      const hasSkipped = skippedCount > 0;
      return (
        <div className="csv-sales-import-modal">
          <div className="csv-sales-import-modal-text">
            {isSuccess
              ? t("pages.integrations.csvImport.steps.result.successTitle")
              : t("pages.integrations.csvImport.steps.result.errorTitle")}
          </div>
          {importReport ? (
            <div className="csv-sales-import-modal-text">
              {t("pages.integrations.csvImport.steps.result.summary", {
                total: totalRows,
                imported: importedCount,
                skipped: skippedCount,
              })}
            </div>
          ) : null}
          <div className="csv-sales-import-modal-text">
            {isSuccess
              ? t("pages.integrations.csvImport.steps.result.successMessage")
              : t("pages.integrations.csvImport.steps.result.errorMessage")}
          </div>
          {isSuccess && hasSkipped ? (
            <div className="csv-sales-import-modal-actions">
              <GenericButton
                label={t(
                  "pages.integrations.csvImport.steps.result.downloadSkipped",
                )}
                onClick={onDownloadSkippedRows}
                theme="secondary"
              />
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className="csv-sales-import-modal">
        <div className="csv-sales-import-modal-text">
          {t("pages.integrations.csvImport.steps.mapping.message")}
        </div>

        <div className="csv-sales-import-modal-mapping-grid">
          {(
            [
              [
                "amount",
                t("pages.integrations.csvImport.fields.amount"),
                false,
              ],
              [
                "closeDate",
                t("pages.integrations.csvImport.fields.closeDate"),
                false,
              ],
              [
                "soldItems",
                t("pages.integrations.csvImport.fields.soldItems"),
                false,
              ],
              [
                "contactEmail",
                t("pages.integrations.csvImport.fields.contactEmail"),
                true,
              ],
              [
                "accountName",
                t("pages.integrations.csvImport.fields.accountName"),
                true,
              ],
            ] as const
          ).map(([key, label, isOptional]) => (
            <div className="csv-sales-import-modal-row" key={key}>
              <label className="csv-sales-import-modal-label">{label}</label>
              <select
                value={(mapping as any)[key] ?? ""}
                onChange={(e) =>
                  setMapping((prev) => ({ ...prev, [key]: e.target.value }))
                }
              >
                {isOptional ? (
                  <option value={NOT_PROVIDED}>
                    {t(
                      "pages.integrations.csvImport.steps.mapping.notProvided",
                    )}
                  </option>
                ) : (
                  <option value="">
                    {t(
                      "pages.integrations.csvImport.steps.mapping.selectColumn",
                    )}
                  </option>
                )}
                {headers.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <div className="csv-sales-import-modal-text">
          {t("pages.integrations.csvImport.steps.mapping.identityHint")}
        </div>
      </div>
    );
  }, [
    headers,
    onSelectFile,
    onDownloadSkippedRows,
    separators.delimiter,
    separators.quote,
    step,
    t,
    mapping,
    result,
    importReport,
  ]);

  return (
    <GenericModal
      isOpen={isOpen}
      title={title}
      onClose={close}
      className="csv-sales-import-modal-container"
    >
      {content}
      {error ? (
        <div className="csv-sales-import-modal-error">{error}</div>
      ) : null}
      <div className="csv-sales-import-modal-footer">
        <div className="csv-sales-import-modal-footer-left">
          {canGoBack && step !== "result" ? (
            <GenericButton
              label={t("pages.integrations.csvImport.actions.back")}
              onClick={goBack}
              theme="secondary"
            />
          ) : (
            <div />
          )}
        </div>

        <div className="csv-sales-import-modal-footer-right">
          {step === "separators" ? (
            <GenericButton
              label={t("pages.integrations.csvImport.actions.next")}
              onClick={onNextFromSeparators}
              theme="primary"
            />
          ) : null}
          {step === "mapping" ? (
            <GenericButton
              label={
                isSubmitting
                  ? t("pages.integrations.csvImport.actions.loading")
                  : t("pages.integrations.csvImport.actions.confirm")
              }
              onClick={onSubmit}
              disabled={!isMappingValid || isSubmitting}
              theme="primary"
            />
          ) : null}
          {step === "result" ? (
            <>
              {result === "error" ? (
                <GenericButton
                  label={t("pages.integrations.csvImport.actions.retry")}
                  onClick={() => setStep("mapping")}
                  theme="secondary"
                />
              ) : null}
              <GenericButton
                label={t("pages.integrations.csvImport.actions.close")}
                onClick={close}
                theme="primary"
              />
            </>
          ) : null}
        </div>
      </div>
    </GenericModal>
  );
};
